import { GridColumn } from "@progress/kendo-react-grid";
import { useNavigate } from "react-router-dom";
import { protectedResources } from '../../authConfig';
import { BaseGrid, ButtonHelper } from '../../components/KendoGrid/BaseGrid';
import { PipetCell, OperatorNameCell, FacilityNameCell } from '../../components/KendoGrid/SharedCells';
import { FilterDescriptor, SortDescriptor, toDataSourceRequestString } from "@progress/kendo-data-query";
import { useContext } from "react";
import { AppUserContext } from "../../components/PvsContextProvider";
import { KitStatus, kitStatuses } from "../../models/kitStatus";
import { DropdownFilterCell } from "../../components/KendoGrid/DropDownFilterCell";
import { useNotificationAPI } from '../../components/NotificationProvider';
import { useFetchWithMsal } from "../../hooks/useFetchWithMsal";
import { BarcodeFilterCell } from "../../components/KendoGrid/StringFilterCell";

// //We will need to translate Received -> Received, Processing, Completed, and Error statuses for members because they don't have access to the full status list
//  For now I am just going to worry about getting the admin version to work
const memberStatusList = [kitStatuses[0], kitStatuses[1], kitStatuses[5]];
const pageTitle = "Submitted Kit List";
interface SubmittedKitListProps {
    labId?: number,
    kitStatusFilter?: number,
    narrowList?: boolean,
    children?: React.ReactNode
}

const SubmittedKitList = (props: SubmittedKitListProps) => {
    const navigate = useNavigate();
    const appUserContext = useContext(AppUserContext);
    const { addNotification } = useNotificationAPI();

    const { execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const recentEntriesFirstSort = [{
        field: "Id",
        dir: "desc"
    } as SortDescriptor];

    let defaultFilter: FilterDescriptor[] | undefined;
    if (props.kitStatusFilter) {
        defaultFilter = [{
            field: "kitStatus",
            operator: "eq",
            value: props.kitStatusFilter,
        }];
    } else if (props.labId) {
        defaultFilter = [{
            field: "LabId",
            operator: "eq",
            value: props.labId,
        }];
    }

    const statusCell = (data: any) => {
        return (
            appUserContext.adminView ?
                <td>
                    {kitStatuses[data.dataItem.kitStatusType.kitStatusId - 1].name}
                </td> :
                <td>
                    {kitStatuses[data.dataItem.kitStatusType.kitStatusId - 1].memberName}
                </td>
        )
    }

    const StatusFilterCell = (props: any) => (
        <DropdownFilterCell
            {...props}
            data={appUserContext.adminView ? kitStatuses : memberStatusList}
            defaultItem={""}
            dataItemKey={"id"}
            textField={appUserContext.adminView ? "name" : "memberName"}
        />
    );

    const enqueueReportEmails = (filter: any) => {
        execute('POST', protectedResources.apiReport.endpoint + "EnqueuePVSReportEmails?" + toDataSourceRequestString(filter)).then((result: any) => {
            if (result?.notifications?.errors.length === 0) {
                addNotification("The job to email out reports has been queued successfully. The job may take several minutes to finish, but you may close the page or continue working.", pageTitle, "success");
            }
        });
    }

    const excelFunction = (submittedKit: any) => {
        const sk = { ...submittedKit };

        if (sk.kitStatusType.kitStatusId >= 1 && sk.kitStatusType.kitStatusId <= 7) {
            sk.kitStatus = kitStatuses[sk.kitStatusType.kitStatusId - 1].name
        }

        return sk
    };

    const gridProps = () => {
        switch (props.kitStatusFilter) {
            case KitStatus.Completed:
                return {
                    button: [{
                        onClick: (filter: any) => { enqueueReportEmails(filter); },
                        buttonText: 'Send Reports',
                    } as ButtonHelper],
                    defaultPageSize: 50
                }
            case undefined:
                return {
                    button: [{
                        onClick: () => { navigate("/submitted-kit-manage"); },
                        buttonText: 'Submit New Kit'
                    } as ButtonHelper],
                }
            default:
                return {}
        }
    };

    const baseGridProps = {
        filter: defaultFilter,
        verb: 'POST',
        pageTitle: 'Submitted Kit List',
        endpoint: protectedResources.apiSubmittedKit.endpoint + "KendoGridRead",
        redirectUrl: "/submitted-kit-manage/",
        intialSort: recentEntriesFirstSort,
        excelFunction: excelFunction,
        key: undefined as undefined | number
    };

    return props.narrowList ?
        <div style={{ width: "820px" }}>
            <BaseGrid {...baseGridProps}>
                <GridColumn field="barcode" title="Barcode Number" filterCell={BarcodeFilterCell} />
                <GridColumn field="pipet.serialNumber" cell={PipetCell} title="Pipet Number" />
                <GridColumn field="pipetDeliveryVolume" title="Volume" filter="numeric" />
                <GridColumn field="dateEntered" title="Date Entered" />
                <GridColumn field="kitStatus" title="Status" cell={statusCell} filterCell={StatusFilterCell} />
            </BaseGrid>
        </div> :
        <BaseGrid {...baseGridProps} {...gridProps()}>
            <GridColumn field="barcode" title="Barcode Number" filterCell={BarcodeFilterCell} />
            <GridColumn field="pipet.serialNumber" cell={PipetCell} title="Pipet Number" />
            <GridColumn field="pipetDeliveryVolume" title="Volume" filter="numeric" />
            <GridColumn field="operator.operatorName" cell={OperatorNameCell} title="Pipet Operator" />
            {!props.labId && 
                <GridColumn field="lab.facilityName" cell={FacilityNameCell} title="Facility Name" />}
            <GridColumn field="dateEntered" title="Date Entered" />
            <GridColumn field="kitStatus" title="Status" cell={statusCell} filterCell={StatusFilterCell} />
        </BaseGrid>
}

export default SubmittedKitList;